import { type WorkspaceAgentBoardMentionSource } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentBoardMentionItem';
import { WORKSPACE_AGENT_BOARD_LIST_OPTIONS } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';
import { type UsePrivateBoardsListParams } from '~/components/v2/hooks/queries/usePrivateBoardsList';
import { GeneralLibrary } from '~/utils/librariesUtils';

/** One infinite `Boards.list` the browse tree mounts as a child, because hooks cannot run in a loop. */
export type WorkspaceAgentBoardListQuerySpec = {
  nodeId: string;
  params: UsePrivateBoardsListParams;
};

/** Enough to pick Global vs a named library vs a board's children — the row's other fields do not change the request. */
export type WorkspaceAgentBoardListQueryNode = {
  id: string;
  kind: 'board' | 'library';
};

export type WorkspaceAgentBoardListSnapshot = {
  boards: readonly WorkspaceAgentBoardMentionSource[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isError: boolean;
  isFetched: boolean;
  isFetching: boolean;
  isFetchNextPageError: boolean;
  isFetchingNextPage: boolean;
  refetch: () => void;
};

/** Next-page is available and not already in flight or failed. A failed page must not keep paging. */
export const isWorkspaceAgentBoardListReadyForNextPage = (
  snapshot: WorkspaceAgentBoardListSnapshot | undefined,
): boolean =>
  !!snapshot?.hasNextPage && !snapshot.isFetchingNextPage && !snapshot.isError && !snapshot.isFetchNextPageError;

export type WorkspaceAgentBoardListSnapshotHandler = (
  nodeId: string,
  snapshot: WorkspaceAgentBoardListSnapshot,
) => void;

const toWorkspaceAgentBoardListParams = (
  params: Omit<UsePrivateBoardsListParams, keyof typeof WORKSPACE_AGENT_BOARD_LIST_OPTIONS | 'workspaceId'> & {
    workspaceId: string;
  },
): UsePrivateBoardsListParams => ({ ...WORKSPACE_AGENT_BOARD_LIST_OPTIONS, ...params });

/** The infinite list under one tree node. Never send `'general'` as `libraryIds`. */
export const toWorkspaceAgentBoardListQuerySpec = (
  node: WorkspaceAgentBoardListQueryNode,
  workspaceId: string,
): WorkspaceAgentBoardListQuerySpec => {
  if (node.kind === 'library') {
    if (node.id === GeneralLibrary.id) {
      return {
        nodeId: node.id,
        params: toWorkspaceAgentBoardListParams({ libraryBoards: 'NO', view: 'root', workspaceId }),
      };
    }

    return {
      nodeId: node.id,
      params: toWorkspaceAgentBoardListParams({
        libraryBoards: 'YES',
        libraryIds: [node.id],
        view: 'root',
        workspaceId,
      }),
    };
  }

  return {
    nodeId: node.id,
    params: toWorkspaceAgentBoardListParams({ view: node.id, workspaceId }),
  };
};
